import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { Button } from "@storybook";

import { Face, apiPipelineCall, useNetwork, useWebAuthentication } from "hooks";
import {
  AuthenticatedState,
  AuthenticatorNameState,
  FacesPipelineState,
  FacesState,
  FacialDataState,
  LoginPhoneNumberState,
  SignInActiveStep,
} from "states";
import { API_URL } from "constant";

import style from "./choose-method.module.sass";
import { Waiting } from "../waiting";
import Modal from "@storybook/new-modal/modal";
import { FaceAuth } from "views/face-auth/face-auth";

interface IChooseMethod {
  onSuccess: Function;
  onFailed: Function;
  onBack: Function;
  locationInfo: any;
  ipAddress: string;
  isFaceOnly?: boolean;
  is_redirect_auth?: string;
  isRecipient: boolean;
}

export interface IPipelineMatch {
  match: boolean;
  enrollmentId: string;
  data: IData;
}

export interface IData {
  match: IMatch;
}

export interface IMatch {
  _label: string;
  _distance: number;
}

export const ChooseMethod: FC<IChooseMethod> = ({
  onFailed,
  onSuccess,
  locationInfo,
  ipAddress,
  onBack,
  isFaceOnly,
  is_redirect_auth,
  isRecipient,
}) => {
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const faceialData = useRecoilValue(FacialDataState);

  const setFaces = useSetRecoilState(FacesState);
  const setPipleineFaces = useSetRecoilState(FacesPipelineState);
  const [authenticated, setAuthenticated] = useRecoilState(AuthenticatedState);
  const setAuthenticatorName = useSetRecoilState(AuthenticatorNameState);

  const { registerNewCredentials, authenticate, getRegistrations } =
    useWebAuthentication();

  const loginPhoneNumber = useRecoilValue(LoginPhoneNumberState);
  const setSignInActiveStep = useSetRecoilState(SignInActiveStep);
  const { phone, countryCode } = useMemo(
    () => loginPhoneNumber,
    [loginPhoneNumber]
  );
  const { post: sendOtp } = useNetwork();

  const handleSuccess = useCallback(
    async (res: any) => {
      if (res) {
        const payload = {
          webAuth: res,
          location: locationInfo,
          ip: ipAddress,
        };
        onSuccess(payload);
        setOpen(false);
      } else onFailed("something went wrong");
    },
    [onFailed, onSuccess, locationInfo, ipAddress]
  );
  const handleFail = useCallback(() => {
    onFailed("Authentication failed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWebAuth = useCallback(() => {
    const isAlreadyExist = getRegistrations().find(
      (item: any) => item.name === phone ?? ""
    );
    const registerPayload = {
      email: phone ?? "",
      id: phone ?? "",
      displayName: phone ?? "",
    };

    if (!isAlreadyExist) registerNewCredentials(handleSuccess, registerPayload);
    else authenticate(handleSuccess, handleFail);
  }, [
    authenticate,
    getRegistrations,
    handleFail,
    handleSuccess,
    phone,
    registerNewCredentials,
  ]);

  const handleFaceLoginSuccess = useCallback(
    async (res: any, recipient_data: any) => {
      console.log("res", res);
      if (res) {
        const payload = {
          matched: res,
          location: locationInfo,
          ip: ipAddress,
          ...(recipient_data ?? {}),
        };
        setOpen(false);
        console.log("&&&&&&&&");
        onSuccess(payload);
      } else onFailed("something went wrong");
    },
    [ipAddress, locationInfo, onFailed, onSuccess]
  );

  const handleOtp = useCallback(() => {
    setSignInActiveStep("otpVerify");

    sendOtp(API_URL.PhoneCodes, {
      phone,
      countryCode,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, countryCode]);

  const handleMobileApp = useCallback(() => {
    setIsWaiting(true);
  }, []);

  const handleGoBack = useCallback(() => {
    if (onBack) {
      setSignInActiveStep("phone");
      onBack();
    } else {
      setSignInActiveStep("phone");
    }
  }, [onBack, setSignInActiveStep]);

  const handleEdit = useCallback(() => {
    setSignInActiveStep("phone");
  }, [setSignInActiveStep]);

  const handleFaceRecognition = useCallback(() => {
    // setSignInActiveStep("face-recognize");
    setOpen(true);
  }, []);

  const renderItem = useMemo(
    () => (
      <div className={style.wrapper}>
        <div className={style.inner_wrapper}>
          <div className={style.inner}>
            <div>
              <div className={style.title}>Choose Method</div>
              <div className={style.sub_title}>
                Select the method through which you want sign in
              </div>
            </div>
            <div className={style.edit}>
              We will send you a confirmation code : {countryCode} {phone}{" "}
              <span className={style.edit_Btn} onClick={handleEdit}>
                Edit
              </span>
            </div>
            <div className={style.lists}>
              {!isFaceOnly && (
                <>
                  <div className={style.list_item} onClick={handleMobileApp}>
                    <div className={style.left_icon}>
                      <i className="ri-smartphone-line"></i>
                    </div>
                    <div className={style.method_title}>
                      Approval from mobile app.
                    </div>
                    <div className={style.right_icon}>
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                  </div>

                  <div className={style.list_item} onClick={handleWebAuth}>
                    <div className={style.left_icon}>
                      <i className="ri-profile-line"></i>
                    </div>
                    <div className={style.method_title}>Device Biometric</div>
                    <div className={style.right_icon}>
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                  </div>

                  <div className={style.list_item} onClick={handleOtp}>
                    <div className={style.left_icon}>
                      <i className="ri-message-line"></i>
                    </div>
                    <div className={style.method_title}>
                      Mobile Text: "One-time password"
                    </div>
                    <div className={style.right_icon}>
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                  </div>
                </>
              )}

              <div className={style.list_item} onClick={handleFaceRecognition}>
                <div className={style.left_icon}>
                  <i className="ri-user-smile-line"></i>
                </div>
                <div className={style.method_title}>Facial Recognition</div>
                <div className={style.right_icon}>
                  <i className="ri-arrow-right-s-line"></i>
                </div>
              </div>
            </div>
            <Button
              type="button__filled--secondary button__large button__block"
              label="Back"
              handleClick={handleGoBack}
            />
          </div>
        </div>
      </div>
    ),
    [
      countryCode,
      handleEdit,
      handleFaceRecognition,
      handleGoBack,
      handleMobileApp,
      handleOtp,
      handleWebAuth,
      isFaceOnly,
      phone,
    ]
  );

  let props = {
    onFailed,
    onSuccess,
    locationInfo,
    ipAddress,
  };

  useEffect(() => {
    if (faceialData?._id) {
      console.log("facialData", faceialData);
      setFaces([]);
      setAuthenticated(false);
      Face.emitter.on(Face.events.FACE_MATCH, (matches: any) => {
        Face.faceFetch = true;

        const face: any = matches[0] ?? { match: {} };
        const label = face.match._label ?? face.match.label;
        const distance = face.match._distance ?? face.match.distance;

        if (distance) {
          if (label === faceialData.name) {
            if (!authenticated) {
              setAuthenticatorName(faceialData.name);
              Face.matchedUserName = faceialData.name;
              Face.matched = true;
              setTimeout(() => {
                setAuthenticated(true);
              }, 150);
            }
          }
          // console.log("--------event Face match Emitter", matches[0]);
        }
        setFaces(matches);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceialData._id]);

  useEffect(() => {
    if (faceialData?._id) {
      setPipleineFaces({});
      setAuthenticated(false);
      // Face.socket.on(faceialData._id, (matches: any) => {
      //   Face.faceFetch = true
      //   console.log("---socket-----", matches[0]);
      //   setFaces(matches);
      // });

      //
      Face.emitter.on(
        Face.events.FACE_MATCH_PIPELINE,
        (matchData: IPipelineMatch) => {
          const { match, data } = matchData ?? {};
          const { _label } = data?.match ?? {};
          Face.faceFetch = true;
          if (match) {
            // console.log("--------event FACE_MATCH_PIPELINE Emitter", matchData);
            // if (_label === faceialData.name) {
            if (!authenticated) {
              setAuthenticatorName(faceialData.name);
              Face.matchedUserName = faceialData.name;
              Face.matched = true;
              setTimeout(() => {
                setAuthenticated(true);
              }, 150);
              // }
            }
            setPipleineFaces(data);
          }
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceialData._id]);

  useEffect(() => {
    if (faceialData?._id) {
      apiPipelineCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceialData._id]);

  return isWaiting ? (
    <Waiting {...props} />
  ) : (
    <Fragment>
      {renderItem}
      <Modal
        isOpen={open}
        modalName="Facial Recognition"
        closeModal={() => setOpen(false)}
      >
        <FaceAuth onSuccess={handleFaceLoginSuccess} />
      </Modal>
    </Fragment>
  );
};
